import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router } from '@angular/router';
import Swal from 'sweetalert2';

import { APIService } from 'src/app/shared/service/apiService.service';
import { ProductsService } from 'src/app/shared/service/products.service';
import {
  SubscriptionService,
  CustomerSubscription,
  SubscriptionPlan,
  RecurringPayment,
} from 'src/app/shared/service/subscription.service';
import {
  BusinessModulesService,
  BusinessToolRow,
  MyModulesResponse,
} from 'src/app/shared/service/business-modules.service';
import { PlanModulesService } from 'src/app/shared/service/plan-modules.service';

function passwordsMatch(group: AbstractControl): ValidationErrors | null {
  const password = group.get('password')?.value;
  const confirm = group.get('confirmPassword')?.value;
  return password === confirm ? null : { mismatch: true };
}

// ── Interfaces para el perfil profesional ─────────────────────────────
export interface EducationItem {
  id: string;
  institution: string;
  degree: string;
  field: string;
  startYear: string;
  endYear: string;
  current: boolean;
  description: string;
}
export interface ExperienceItem {
  id: string;
  company: string;
  role: string;
  area: string;
  startDate: string;
  endDate: string;
  current: boolean;
  description: string;
}
export interface ProjectItem {
  id: string;
  title: string;
  description: string;
  url: string;
  imageUrl: string;
  tags: string;
}

function emptyEducation(): EducationItem {
  return { id: '', institution: '', degree: '', field: '', startYear: '', endYear: '', current: false, description: '' };
}
function emptyExperience(): ExperienceItem {
  return { id: '', company: '', role: '', area: '', startDate: '', endDate: '', current: false, description: '' };
}
function emptyProject(): ProjectItem {
  return { id: '', title: '', description: '', url: '', imageUrl: '', tags: '' };
}

const STORAGE_KEY_PROFILE = 'learnify_profile_extra';

@Component({
	selector: 'app-studentprofilemain',
	templateUrl: './studentprofilemain.component.html',
	styleUrls: ['./studentprofilemain.component.scss']
})
export class StudentprofilemainComponent implements OnInit {
	@ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

	menuAbierto = false;
	nombreMenuSeleccionado = 'Editar perfil';
	editMode = false;
	selectedTab = 0;

	// Sub-tabs dentro de "Editar perfil"
	profileSubTab = 0;

	// Perfil profesional
	profileCategory = '';
	bannerUrl = '';
	readonly CATEGORIES = [
		'Marketing', 'Tecnología', 'Diseño', 'Negocios', 'Educación',
		'Salud', 'Arte y Creatividad', 'Finanzas', 'Derecho', 'Otro',
	];

	// Educación
	educationItems: EducationItem[] = [];
	showEducationForm = false;
	educationForm: EducationItem = emptyEducation();
	editingEducationIdx = -1;

	// Experiencia
	experienceItems: ExperienceItem[] = [];
	showExperienceForm = false;
	experienceForm: ExperienceItem = emptyExperience();
	editingExperienceIdx = -1;

	// Proyectos
	projectItems: ProjectItem[] = [];
	showProjectForm = false;
	projectForm: ProjectItem = emptyProject();
	editingProjectIdx = -1;

	datosUsuario: any = {};
	idUsuario: number | null = null;
	passwordForm!: FormGroup;
	changingPassword = false;

	subscription: CustomerSubscription | null = null;
	availablePlans: SubscriptionPlan[] = [];
	loadingSubscription = false;

	payments: RecurringPayment[] = [];
	paymentsLoading = false;
	paymentsPage = 0;
	paymentsTotalPages = 0;
	paymentsTotalCount = 0;
	paymentsLimit = 10;
	showPlanPicker = false;

	// ── Tab: Mis herramientas ────────────────────────────────────────
	toolsLoading = false;
	toolsLoaded = false;
	toolsData: MyModulesResponse | null = null;
	toolsSelected = new Set<string>();
	toolsSaving = false;

	// Panel "Configurar mis herramientas" dentro de Mi suscripción
	showToolsPanel = false;

	private readonly tabNames = [
		'Editar perfil',
		'Actualizar contraseña',
		'Mi suscripción',
		'Historial de pagos',
	];

	constructor(
		private apiService: APIService,
		private productsService: ProductsService,
		private subscriptionService: SubscriptionService,
		private businessModules: BusinessModulesService,
		private planModules: PlanModulesService,
		private router: Router,
	) {}

	ngOnInit(): void {
		const raw = localStorage.getItem('currentUser');
		if (!raw) {
			this.router.navigate(['/auth/sign-in']);
			return;
		}

		try {
			const user = JSON.parse(raw);
			this.idUsuario = user?.id ?? null;
		} catch {
			this.idUsuario = null;
		}

		if (!this.idUsuario) {
			this.router.navigate(['/auth/sign-in']);
			return;
		}

		this.cargarPerfil();
		this.initPasswordForm();
		this.loadSubscription();
		this.loadAvailablePlans();
		this.loadProfileExtra();
	}

	cargarPerfil(): void {
		if (!this.idUsuario) return;

		this.apiService.getProfile().subscribe({
			next: (data: any) => {
				this.datosUsuario = data || {};

				let imageUrl = this.datosUsuario.profileFileUrl;
				if (imageUrl && (imageUrl.startsWith('/var/') || imageUrl.startsWith('/usr/') || /^\d+(\.\d+)?$/.test(imageUrl))) {
					imageUrl = null;
				}
				if (!imageUrl && this.datosUsuario.profileImage?.base64) {
					imageUrl = `data:image/jpeg;base64,${this.datosUsuario.profileImage.base64}`;
				}
				this.datosUsuario.profileFileUrl = imageUrl || 'assets/images/user.png';

				const email = this.datosUsuario.email;
				if (email && this.passwordForm) {
					this.passwordForm.patchValue({ email });
				}
			},
			error: () => Swal.fire('Error', 'No se pudo cargar tu perfil', 'error'),
		});
	}

	toggleMenu(): void {
		this.menuAbierto = !this.menuAbierto;
	}

	selectTab(index: number): void {
		this.selectedTab = index;
		this.nombreMenuSeleccionado = this.tabNames[index] || 'Editar perfil';
		this.menuAbierto = false;

		if (index === 2) {
			if (!this.subscription && !this.loadingSubscription) this.loadSubscription();
			if (!this.toolsLoaded) this.loadMyTools();
		}
		if (index === 3 && !this.payments.length && !this.paymentsLoading) {
			this.loadPaymentHistory();
		}
	}

	// ── Mis herramientas (panel dentro de Mi suscripción) ────────────
	toggleToolsPanel(): void {
		this.showToolsPanel = !this.showToolsPanel;
	}

	loadMyTools(): void {
		this.toolsLoading = true;
		this.businessModules.getMyModules().subscribe({
			next: (response) => {
				this.toolsData = response;
				this.toolsSelected = new Set(response.enabledModules || []);
				this.toolsLoading = false;
				this.toolsLoaded = true;
			},
			error: () => {
				this.toolsLoading = false;
				Swal.fire('Error', 'No se pudieron cargar tus herramientas.', 'error');
			},
		});
	}

	get toolsMaxModules(): number { return this.toolsData?.maxModules ?? 0; }
	get toolsActiveCount(): number { return this.toolsSelected.size; }
	get toolsRemaining(): number { return Math.max(0, this.toolsMaxModules - this.toolsActiveCount); }

	isToolSelected(tool: BusinessToolRow): boolean { return this.toolsSelected.has(tool.key); }

	isToolDisabled(tool: BusinessToolRow): boolean {
		if (!tool.inPool) return true;
		return !this.isToolSelected(tool) && this.toolsActiveCount >= this.toolsMaxModules;
	}

	toggleTool(tool: BusinessToolRow, checked: boolean): void {
		if (!tool.inPool) return;
		if (checked) {
			if (this.toolsActiveCount >= this.toolsMaxModules) return;
			this.toolsSelected.add(tool.key);
		} else {
			this.toolsSelected.delete(tool.key);
		}
	}

	saveMyTools(): void {
		if (!this.toolsData) return;
		if (this.toolsSelected.size === 0) {
			Swal.fire('Selección requerida', 'Activa al menos una herramienta.', 'warning');
			return;
		}
		if (this.toolsSelected.size > this.toolsMaxModules) {
			Swal.fire('Límite excedido', `Tu plan permite hasta ${this.toolsMaxModules} herramienta(s).`, 'warning');
			return;
		}
		this.toolsSaving = true;
		this.businessModules.updateMyModules(Array.from(this.toolsSelected)).subscribe({
			next: (response) => {
				this.toolsData = response;
				this.toolsSelected = new Set(response.enabledModules || []);
				this.planModules.applyModuleSelection(response);
				this.toolsSaving = false;
				Swal.fire('¡Guardado!', response.message || 'Tus herramientas fueron actualizadas.', 'success');
			},
			error: (err) => {
				this.toolsSaving = false;
				Swal.fire('Error', err?.error?.message || 'No se pudieron guardar las herramientas.', 'error');
			},
		});
	}

	toggleEditMode(): void {
		if (this.editMode) {
			this.onSubmit();
		} else {
			this.editMode = true;
		}
	}

	onSubmit(): void {
		if (!this.idUsuario) return;
		const body = {
			firstName: this.datosUsuario.firstName,
			lastName: this.datosUsuario.lastName,
			displayName: `${this.datosUsuario.firstName || ''} ${this.datosUsuario.lastName || ''}`.trim(),
			phoneNumber: this.datosUsuario.phoneNumber,
			biography: this.datosUsuario.biography,
		};

		Swal.fire({ title: '¿Guardar cambios?', showCancelButton: true, confirmButtonText: 'Confirmar' })
			.then(res => {
				if (res.isConfirmed) {
					this.productsService.updateProfile(this.idUsuario!, body).subscribe({
						next: () => {
							Swal.fire('Datos actualizados', '', 'success');
							this.editMode = false;
							this.cargarPerfil();
							try {
								const current = JSON.parse(localStorage.getItem('currentUser') || '{}');
								localStorage.setItem('currentUser', JSON.stringify({ ...current, ...body }));
							} catch {}
						},
						error: () => Swal.fire('Error', 'No se pudo actualizar tu perfil', 'error'),
					});
				}
			});
	}

	triggerFileInput(): void {
		this.fileInput?.nativeElement?.click();
	}

	onFileSelected(event: any): void {
		const file: File = event?.target?.files?.[0];
		if (!file || !this.idUsuario) return;

		const v = this.productsService.validateProfileImageFile(file);
		if (!v.valid) {
			Swal.fire('Archivo inválido', v.error || 'Formato o tamaño no permitido', 'error');
			event.target.value = '';
			return;
		}

		const formData = new FormData();
		formData.append('photo', file);

		Swal.fire({ title: 'Subiendo imagen...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });

		this.productsService.uploadProfilePhoto(formData).subscribe({
			next: (response: any) => {
				Swal.close();
				const imageUrl = response?.url || response?.profileFileUrl || response?.ImageUrl;
				if (!imageUrl) {
					Swal.fire('Error', 'No se recibió URL de imagen', 'error');
					return;
				}
				this.datosUsuario.profileFileUrl = imageUrl;
				Swal.fire('Éxito', 'Imagen de perfil actualizada', 'success');
			},
			error: (err) => {
				Swal.close();
				Swal.fire('Error', err?.message || 'No se pudo subir la imagen', 'error');
			},
		});
	}

	initPasswordForm(): void {
		const email = this.datosUsuario?.email || JSON.parse(localStorage.getItem('currentUser') || '{}')?.email || '';
		this.passwordForm = new FormGroup({
			email: new FormControl({ value: email, disabled: true }, [Validators.required, Validators.email]),
			password: new FormControl('', [Validators.required, Validators.minLength(8)]),
			confirmPassword: new FormControl('', [Validators.required]),
		}, { validators: passwordsMatch });
	}

	updatePassword(): void {
		if (this.passwordForm.invalid || !this.idUsuario) return;

		this.changingPassword = true;
		const email = this.datosUsuario?.email || this.passwordForm.getRawValue().email;
		const password = this.passwordForm.get('password')?.value;

		this.apiService.changePassword({ email, password }).subscribe({
			next: () => {
				this.changingPassword = false;
				Swal.fire('Contraseña actualizada', 'Tu contraseña fue cambiada correctamente.', 'success');
				this.passwordForm.patchValue({ password: '', confirmPassword: '' });
				this.passwordForm.markAsPristine();
			},
			error: () => {
				this.changingPassword = false;
				Swal.fire('Error', 'No se pudo actualizar la contraseña.', 'error');
			},
		});
	}

	nextPaymentLabel(): string {
		if (!this.subscription) return '—';
		if (this.subscription.status === 'trial' && this.subscription.trial_end_date) {
			return this.formatDate(this.subscription.trial_end_date);
		}
		if (this.subscription.current_period_end) {
			return this.formatDate(this.subscription.current_period_end);
		}
		return '—';
	}

	repeatUnitLabel(unit?: string): string {
		return unit === 'year' ? 'anual' : 'mensual';
	}

	togglePlanPicker(): void {
		this.showPlanPicker = !this.showPlanPicker;
	}

	private formatDate(value: string): string {
		try {
			return new Date(value).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });
		} catch {
			return value;
		}
	}

	loadSubscription(): void {
		if (!this.idUsuario) return;
		this.loadingSubscription = true;
		this.subscriptionService.getMySubscription(this.idUsuario).subscribe({
			next: (res) => {
				this.subscription = res.subscription;
				this.loadingSubscription = false;
			},
			error: () => {
				this.subscription = null;
				this.loadingSubscription = false;
			},
		});
	}

	loadAvailablePlans(): void {
		this.subscriptionService.getPlans().subscribe({
			next: (plans) => { this.availablePlans = plans; },
		});
	}

	loadPaymentHistory(): void {
		if (!this.idUsuario) return;
		this.paymentsLoading = true;
		this.subscriptionService.getPaymentHistory(this.idUsuario, this.paymentsPage, this.paymentsLimit).subscribe({
			next: (res) => {
				this.payments = res.rows;
				this.paymentsTotalCount = res.count;
				this.paymentsTotalPages = res.numPages;
				this.paymentsLoading = false;
			},
			error: () => {
				this.payments = [];
				this.paymentsLoading = false;
			},
		});
	}

	previousPaymentsPage(): void {
		if (this.paymentsPage > 0) {
			this.paymentsPage--;
			this.loadPaymentHistory();
		}
	}

	nextPaymentsPage(): void {
		if (this.paymentsPage < this.paymentsTotalPages - 1) {
			this.paymentsPage++;
			this.loadPaymentHistory();
		}
	}

	subscriptionStatusLabel(status?: string): string {
		const map: Record<string, string> = {
			trial: 'Prueba gratuita',
			active: 'Activa',
			past_due: 'Pago vencido',
			paused: 'Pausada',
			cancelled: 'Cancelada',
		};
		return status ? (map[status] || status) : '';
	}

	subscriptionStatusClass(status?: string): string {
		const map: Record<string, string> = {
			trial: 'bg-warning text-dark',
			active: 'bg-success',
			past_due: 'bg-danger',
			paused: 'bg-secondary',
			cancelled: 'bg-dark',
		};
		return status ? (map[status] || 'bg-secondary') : 'bg-secondary';
	}

	paymentStatusLabel(status: string): string {
		const map: Record<string, string> = {
			completed: 'Pagado',
			failed: 'Fallido',
			pending: 'Pendiente',
			refunded: 'Reembolsado',
		};
		return map[status] || status;
	}

	paymentStatusClass(status: string): string {
		const map: Record<string, string> = {
			completed: 'bg-success',
			failed: 'bg-danger',
			pending: 'bg-warning text-dark',
			refunded: 'bg-info text-dark',
		};
		return map[status] || 'bg-secondary';
	}

	downloadReceipt(payment: RecurringPayment): void {
		if (payment.cfdi_url) window.open(payment.cfdi_url, '_blank');
	}

	upgradePlan(planId: number): void {
		if (!this.subscription) return;
		Swal.fire({
			title: '¿Actualizar plan?',
			text: 'Se actualizará tu suscripción a partir del siguiente ciclo.',
			icon: 'question',
			showCancelButton: true,
			confirmButtonText: 'Sí, actualizar',
			cancelButtonText: 'Cancelar',
		}).then((result) => {
			if (result.isConfirmed) {
				this.subscriptionService.upgradePlan(this.subscription!.id, planId).subscribe({
					next: () => {
						Swal.fire('¡Listo!', 'Tu plan fue actualizado.', 'success');
						this.showPlanPicker = false;
						this.loadSubscription();
					},
					error: () => Swal.fire('Error', 'No se pudo actualizar el plan.', 'error'),
				});
			}
		});
	}

	goToPlans(): void {
		this.router.navigate(['/suscripciones/planes']);
	}

	// ── Perfil profesional — carga / guardado via API ─────────────────────
	loadProfileExtra(): void {
		if (!this.idUsuario) return;
		this.apiService.getProfileExtra(this.idUsuario).subscribe({
			next: (res) => {
				const data = res?.profileExtra;
				if (!data) return;
				this.profileCategory = data.category   || '';
				this.bannerUrl       = data.bannerUrl  || '';
				this.educationItems  = data.education  || [];
				this.experienceItems = data.experience || [];
				this.projectItems    = data.projects   || [];
				// Respaldo local para uso offline (solo lectura)
				try { localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify(data)); } catch {}
			},
			error: () => {
				// Si la API falla (offline / sin columna aún migrada) carga desde cache local
				this._loadProfileExtraFromCache();
			},
		});
	}

	private _loadProfileExtraFromCache(): void {
		try {
			const raw = localStorage.getItem(STORAGE_KEY_PROFILE);
			if (!raw) return;
			const data = JSON.parse(raw);
			this.profileCategory = data.category || '';
			this.bannerUrl       = data.bannerUrl  || '';
			this.educationItems  = data.education  || [];
			this.experienceItems = data.experience || [];
			this.projectItems    = data.projects   || [];
		} catch { /* ignore */ }
	}

	private saveProfileExtra(onSuccess?: () => void): void {
		if (!this.idUsuario) return;
		const payload = {
			category:   this.profileCategory,
			bannerUrl:  this.bannerUrl,
			education:  this.educationItems,
			experience: this.experienceItems,
			projects:   this.projectItems,
		};
		this.apiService.updateProfileExtra(this.idUsuario, payload).subscribe({
			next: () => {
				// Actualizar cache local
				try { localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify(payload)); } catch {}
				if (onSuccess) onSuccess();
			},
			error: (err) => {
				console.error('Error guardando perfil extra:', err);
				// Fallback: guardar localmente para no perder el dato
				try { localStorage.setItem(STORAGE_KEY_PROFILE, JSON.stringify(payload)); } catch {}
				if (onSuccess) onSuccess(); // continuar aunque falle el servidor
			},
		});
	}

	// ── Educación ────────────────────────────────────────────────────────────
	openAddEducation(): void {
		this.educationForm    = emptyEducation();
		this.editingEducationIdx = -1;
		this.showEducationForm   = true;
	}

	openEditEducation(idx: number): void {
		this.educationForm    = { ...this.educationItems[idx] };
		this.editingEducationIdx = idx;
		this.showEducationForm   = true;
	}

	saveEducation(): void {
		const item: EducationItem = { ...this.educationForm, id: this.educationForm.id || Date.now().toString() };
		if (this.editingEducationIdx >= 0) {
			this.educationItems[this.editingEducationIdx] = item;
		} else {
			this.educationItems = [item, ...this.educationItems];
		}
		this.showEducationForm = false;
		this.saveProfileExtra();
	}

	removeEducation(idx: number): void {
		this.educationItems = this.educationItems.filter((_, i) => i !== idx);
		this.saveProfileExtra();
	}

	// ── Experiencia ──────────────────────────────────────────────────────────
	openAddExperience(): void {
		this.experienceForm    = emptyExperience();
		this.editingExperienceIdx = -1;
		this.showExperienceForm   = true;
	}

	openEditExperience(idx: number): void {
		this.experienceForm    = { ...this.experienceItems[idx] };
		this.editingExperienceIdx = idx;
		this.showExperienceForm   = true;
	}

	saveExperience(): void {
		const item: ExperienceItem = { ...this.experienceForm, id: this.experienceForm.id || Date.now().toString() };
		if (this.editingExperienceIdx >= 0) {
			this.experienceItems[this.editingExperienceIdx] = item;
		} else {
			this.experienceItems = [item, ...this.experienceItems];
		}
		this.showExperienceForm = false;
		this.saveProfileExtra();
	}

	removeExperience(idx: number): void {
		this.experienceItems = this.experienceItems.filter((_, i) => i !== idx);
		this.saveProfileExtra();
	}

	// ── Proyectos / Casos de éxito ───────────────────────────────────────────
	openAddProject(): void {
		this.projectForm    = emptyProject();
		this.editingProjectIdx = -1;
		this.showProjectForm   = true;
	}

	openEditProject(idx: number): void {
		this.projectForm    = { ...this.projectItems[idx] };
		this.editingProjectIdx = idx;
		this.showProjectForm   = true;
	}

	saveProject(): void {
		const item: ProjectItem = { ...this.projectForm, id: this.projectForm.id || Date.now().toString() };
		if (this.editingProjectIdx >= 0) {
			this.projectItems[this.editingProjectIdx] = item;
		} else {
			this.projectItems = [item, ...this.projectItems];
		}
		this.showProjectForm = false;
		this.saveProfileExtra();
	}

	removeProject(idx: number): void {
		this.projectItems = this.projectItems.filter((_, i) => i !== idx);
		this.saveProfileExtra();
	}

	saveProfessionalProfile(): void {
		this.saveProfileExtra(() => {
			Swal.fire({ icon: 'success', title: 'Perfil guardado', timer: 1500, showConfirmButton: false });
		});
	}

	logOut(): void {
		Swal.fire({ title: '¿Seguro quieres cerrar sesión?', showCancelButton: true, confirmButtonText: 'Confirmar' })
			.then(res => {
				if (res.isConfirmed) {
					try { localStorage.removeItem('currentUser'); } catch {}
					this.router.navigate(['/auth/sign-in']);
					Swal.fire('Sesión cerrada', '', 'success');
				}
			});
	}
}
